import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Copy, Check, ArrowLeft, Sparkles, Lock } from 'lucide-react';
import { PromptPaywall, isPromptyUnlocked, setPromptyUnlocked } from '../components/PromptPaywall';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { trackPlausibleEvent } from '../utils/tracking';

interface PromptDetail {
    id: number;
    slug: string;
    title: string;
    category?: string;
    tool?: string;
    difficulty?: string;
    tags?: string[];
    prompt_pl: string;
    prompt_pl_locked?: boolean;
    caption_pl?: string;
    cta_pl?: string;
    source_url?: string;
    source_platform?: string;
    source_channel?: string;
    is_premium: number;
    graphic_url?: string;
}

const toolLabels: Record<string, string> = {
    chatgpt: 'ChatGPT', claude: 'Claude', midjourney: 'Midjourney',
    sora: 'Sora', gemini: 'Gemini', agnostic: 'Każdy AI',
};

const PromptDetailMeta: React.FC<{ prompt: PromptDetail }> = ({ prompt }) => {
    usePageMetadata({
        title: `${prompt.title} | Prompty AI dla biznesu | BiznesBezKlikania`,
        description: prompt.caption_pl || `Prompt AI: ${prompt.title}. Gotowy do skopiowania, sprawdzony pod e-commerce i MŚP.`,
        ogData: { type: 'article' },
        schema: {
            '@context': 'https://schema.org',
            '@type': 'HowTo',
            'name': prompt.title,
            'description': prompt.caption_pl,
            'step': [{ '@type': 'HowToStep', 'name': 'Skopiuj prompt', 'text': prompt.prompt_pl.slice(0, 300) }],
        },
    });
    return null;
};

export const PromptyDetailPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [prompt, setPrompt] = useState<PromptDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [unlocked, setUnlocked] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const u = isPromptyUnlocked();
        setUnlocked(u);
    }, []);

    useEffect(() => {
        if (!slug) return;
        setLoading(true);
        const url = `/api/prompty/${slug}${unlocked ? '?unlocked=1' : ''}`;
        fetch(url)
            .then(r => r.ok ? r.json() : null)
            .then(d => setPrompt(d))
            .catch(err => console.error('[prompty/detail]', err))
            .finally(() => setLoading(false));
    }, [slug, unlocked]);

    function copyPrompt() {
        if (!prompt || prompt.prompt_pl_locked) return;
        navigator.clipboard.writeText(prompt.prompt_pl);
        setCopied(true);
        fetch(`/api/prompty/${slug}/copy`, { method: 'POST' }).catch(() => {});
        trackPlausibleEvent('prompty_copy', { prompt_slug: slug });
        setTimeout(() => setCopied(false), 2000);
    }

    if (loading) {
        return <div className="container mx-auto px-4 py-20 text-center text-gray-500">Wczytuję prompt…</div>;
    }

    if (!prompt) {
        return (
            <div className="container mx-auto px-4 py-20 text-center">
                <h1 className="text-3xl font-bold mb-4">Prompt nie znaleziony</h1>
                <Link to="/prompty" className="text-blue-600 hover:underline">← Wróć do biblioteki</Link>
            </div>
        );
    }

    const isLocked = prompt.is_premium === 1 && !unlocked;

    return (
        <>
            <PromptDetailMeta prompt={prompt} />
            <div className="container mx-auto px-4 py-8 max-w-3xl">
                <Link to="/prompty" className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 mb-6">
                    <ArrowLeft className="w-4 h-4" /> Biblioteka promptów
                </Link>

                {/* Header */}
                <div className="mb-8">
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                        {prompt.tool && (
                            <span className="bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-medium px-2.5 py-1 rounded-full">
                                {toolLabels[prompt.tool] || prompt.tool}
                            </span>
                        )}
                        {prompt.category && (
                            <span className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs font-medium px-2.5 py-1 rounded-full">
                                {prompt.category}
                            </span>
                        )}
                        {prompt.difficulty && (
                            <span className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs font-medium px-2.5 py-1 rounded-full">
                                {prompt.difficulty === 'basic' ? 'Podstawowy' : prompt.difficulty === 'intermediate' ? 'Średni' : 'Zaawansowany'}
                            </span>
                        )}
                        {prompt.is_premium === 1 ? (
                            <span className="bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1">
                                <Lock className="w-3 h-3" /> Premium
                            </span>
                        ) : (
                            <span className="bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-xs font-semibold px-2.5 py-1 rounded-full">
                                Darmowy
                            </span>
                        )}
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-bold mb-4">{prompt.title}</h1>
                    {prompt.caption_pl && (
                        <p className="text-lg text-gray-600 dark:text-gray-400">{prompt.caption_pl}</p>
                    )}
                </div>

                {/* Graphic */}
                {prompt.graphic_url && (
                    <div className="mb-8 rounded-2xl overflow-hidden bg-gradient-to-br from-blue-50 to-violet-50 dark:from-gray-800 dark:to-gray-700">
                        <img src={prompt.graphic_url} alt={prompt.title} className="w-full" />
                    </div>
                )}

                {/* Prompt body */}
                <div className="bg-gray-900 dark:bg-gray-950 rounded-2xl p-6 mb-6 relative">
                    <div className="flex items-center justify-between mb-3 text-xs text-gray-400">
                        <span className="flex items-center gap-2">
                            <Sparkles className="w-3 h-3" /> Prompt po polsku
                        </span>
                        {!isLocked && (
                            <button
                                onClick={copyPrompt}
                                className="flex items-center gap-1.5 text-xs text-gray-300 hover:text-white bg-white/10 px-3 py-1.5 rounded-full transition-colors"
                            >
                                {copied ? <><Check className="w-3 h-3" /> Skopiowane!</> : <><Copy className="w-3 h-3" /> Skopiuj</>}
                            </button>
                        )}
                    </div>
                    <pre className="whitespace-pre-wrap text-sm text-gray-100 font-mono leading-relaxed">{prompt.prompt_pl}</pre>
                    {isLocked && (
                        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-gray-900 dark:from-gray-950 to-transparent pointer-events-none" />
                    )}
                </div>

                {/* Paywall if locked */}
                {isLocked && (
                    <div className="mb-8">
                        <PromptPaywall
                            variant="inline"
                            promptSlug={prompt.slug}
                            promptTitle={prompt.title}
                            onUnlocked={() => { setUnlocked(true); setPromptyUnlocked(); }}
                        />
                    </div>
                )}

                {/* Related CTA */}
                <div className="border-t border-gray-200 dark:border-gray-800 pt-8">
                    <h3 className="font-bold text-lg mb-3">Chcesz tego AI w swojej firmie?</h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                        Zamiast kopiować prompty ręcznie — postaw u siebie agenta AI który odpowiada klientom, robi opisy produktów i prowadzi rozmowy.
                    </p>
                    <Link
                        to="/produkty"
                        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-full transition-colors"
                    >
                        Zobacz produkty BBK →
                    </Link>
                </div>
            </div>
        </>
    );
};
